import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Ticket, TicketMessage } from "@/lib/types";
import { Head, Measure } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { Button } from "@/ui/Button";
import { Strip, Reading } from "@/ui/Strip";
import { Lamp } from "@/ui/Lamp";
import { ErrorState, Skeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";
import { Conversation } from "@/features/support/Conversation";
import { CATEGORY_LABEL, isOpen } from "@/features/support/tickets";
import { TicketServices } from "@/features/support/TicketServices";
import { TicketStatusPill } from "./o.$orgId.support.index";

export const Route = createFileRoute("/_app/o/$orgId/support/$ticketId")({
  component: TicketScreen,
});

interface TicketView {
  ticket: Ticket;
  messages: TicketMessage[];
}

/** One support request, and the conversation on it. */
function TicketScreen() {
  const { orgId, ticketId } = Route.useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const key = ["ticket", orgId, ticketId];

  const view = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      api.get<TicketView>(`/organisations/${orgId}/tickets/${ticketId}`, undefined, signal),
    // While a ticket is waiting on us, an answer can arrive at any moment and
    // the person reading is often the person waiting for it. Thirty seconds
    // is often enough to notice and rare enough to cost nothing.
    refetchInterval: (q) =>
      q.state.data && isOpen(q.state.data.ticket.status) ? 30_000 : false,
  });

  const reply = useMutation({
    mutationFn: (body: string) =>
      api.post<{ ticket: Ticket }>(`/organisations/${orgId}/tickets/${ticketId}/messages`, {
        body,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (e) => toast.error(e),
  });

  const resolve = useMutation({
    mutationFn: () =>
      api.post<{ ticket: Ticket }>(`/organisations/${orgId}/tickets/${ticketId}/resolve`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ["tickets", orgId] });
      toast.success("Marked as resolved", "Reply here if you need to reopen it.");
    },
    onError: (e) => toast.error(e),
  });

  if (view.isError) {
    return (
      <div className="flex flex-col gap-6">
        <Head title="Support" />
        <ErrorState error={view.error} onRetry={() => void view.refetch()} />
      </div>
    );
  }

  const t = view.data?.ticket;
  const messages = view.data?.messages ?? [];
  const closed = t?.status === "closed";

  return (
    <div className="flex flex-col gap-6">
      <Head
        above={
          <Trail
            trail={[
              { label: "Support", to: "/o/$orgId/support", params: { orgId } },
              { label: t?.reference ?? "…" },
            ]}
          />
        }
        title={t?.subject ?? <Skeleton className="h-8 w-80" />}
        actions={
          t && isOpen(t.status) ? (
            <Button
              tone="secondary"
              busy={resolve.isPending}
              onClick={() => resolve.mutate()}
            >
              This is resolved
            </Button>
          ) : undefined
        }
      />

      {/* The facts across the top, the conversation at a reading measure
          under them. They used to be a strip of metadata between the title
          and the first message — a line read once and then scrolled past
          forever, sitting in the position the conversation wanted — and then
          a 320px column beside a thread that needs 68 characters, not 900
          pixels. The strip is the shape that serves both. */}
      {t && (
        <Strip>
          <Reading label="Status">
            <TicketStatusPill status={t.status} />
          </Reading>
          <Reading label="Reference">{t.reference}</Reading>
          <Reading label="About">{CATEGORY_LABEL[t.category]}</Reading>
          <Reading label="Opened by">{t.opened_by_name}</Reading>
          <Reading label="Opened">
            <span title={absoluteTime(t.created_at)}>{relativeTime(t.created_at)}</span>
          </Reading>
          {!t.first_reply_at && isOpen(t.status) && (
            <Reading label="Answer" lamp={<Lamp state="warn" />}>
              <span className="text-[var(--warning)]">not yet</span>
            </Reading>
          )}
        </Strip>
      )}

      <Measure>
        {/* Shown to the customer too, not only to staff. It is the record of
            what they told us, and on a request they come back to a week later
            it is the fastest answer to "which one was this about". */}
        {t?.services && t.services.length > 0 && (
          <div className="mb-6">
            <p className="section-label mb-2">Services this is about</p>
            <TicketServices services={t.services} orgId={orgId} />
          </div>
        )}

        {view.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" rounded="lg" />
            <Skeleton className="h-24 w-full" rounded="lg" />
          </div>
        ) : (
          <Conversation
            messages={messages}
            sending={reply.isPending}
            disabled={closed}
            disabledReason={
              "This ticket is closed. Open a new request and mention " +
              (t?.reference ?? "it") +
              "."
            }
            onSend={(body) => reply.mutate(body)}
          />
        )}
      </Measure>
    </div>
  );
}
